import { useState, useEffect } from "react";
import { Mail, CheckCircle, ArrowRight, RefreshCw } from "lucide-react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Link, useLocation, Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { authApi } from "@/services/api";

export const RegistrationSuccess = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const email = location.state?.email;
  const [resending, setResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  // Cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(
        () => setResendCooldown(resendCooldown - 1),
        1000,
      );
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // If no email in state, redirect to register
  if (!email) {
    return <Navigate to="/register" replace />;
  }

  const handleResendEmail = async () => {
    if (resendCooldown > 0) return;

    setResending(true);
    try {
      await authApi.resendVerification({ email });
      toast.success(
        t("auth.registrationSuccess.emailResent", "Verification email resent!"),
      );
      setResendCooldown(60);
    } catch (error) {
      toast.error(
        error.response?.data?.error?.message ||
          t(
            "auth.registrationSuccess.resendError",
            "Failed to resend email. Please try again.",
          ),
      );
    } finally {
      setResending(false);
    }
  };

  const renderButtonTitle = () => {
    if (resending) {
      return (
        <>
          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
          {t("auth.registrationSuccess.sending", "Sending...")}
        </>
      );
    }
    return resendCooldown > 0 ? (
      t("auth.registrationSuccess.resendIn", "Resend in {{seconds}}s", {
        seconds: resendCooldown,
      })
    ) : (
      <>
        <RefreshCw className="w-4 h-4 mr-2" />
        {t("auth.registrationSuccess.resendEmail", "Resend verification email")}
      </>
    );
  };

  return (
    <div className="w-full max-w-md mx-auto text-center">
      {/* Success Icon */}
      <div className="mb-6 flex justify-center">
        <div className="relative">
          <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center">
            <Mail className="w-10 h-10 text-primary" />
          </div>
          <div className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-green-500 flex items-center justify-center">
            <CheckCircle className="w-5 h-5 text-white" />
          </div>
        </div>
      </div>

      {/* Title */}
      <h1 className="text-2xl font-bold text-foreground mb-2">
        {t("auth.registrationSuccess.title", "Check Your Email")}
      </h1>

      {/* Description */}
      <p className="text-muted-foreground mb-6">
        {t(
          "auth.registrationSuccess.description",
          "We've sent a verification link to your email address. Please click the link to verify your account and complete registration.",
        )}
      </p>

      {/* Email Display */}
      <div className="bg-muted/50 rounded-lg p-4 mb-6">
        <p className="text-sm text-muted-foreground mb-1">
          {t("auth.registrationSuccess.sentTo", "Sent to:")}
        </p>
        <p className="font-medium text-foreground">{email}</p>
      </div>

      {/* Instructions */}
      <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 mb-6 text-left">
        <h3 className="font-medium text-foreground mb-2">
          {t("auth.registrationSuccess.whatNext", "What's next?")}
        </h3>
        <ol className="space-y-2 text-sm text-foreground/80 list-decimal list-inside">
          <li>
            {t("auth.registrationSuccess.step1", "Open your email inbox")}
          </li>
          <li>
            {t(
              "auth.registrationSuccess.step2",
              "Look for an email from eScribe",
            )}
          </li>
          <li>
            {t(
              "auth.registrationSuccess.step3",
              "Click the verification link in the email",
            )}
          </li>
        </ol>
      </div>

      {/* Didn't receive email */}
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground">
          {t("auth.registrationSuccess.noEmail", "Didn't receive the email?")}
        </p>

        <Button
          variant="outline"
          onClick={handleResendEmail}
          disabled={resending || resendCooldown > 0}
          className="w-full"
        >
          {renderButtonTitle()}
        </Button>

        <p className="text-xs text-muted-foreground">
          {t(
            "auth.registrationSuccess.checkSpam",
            "Make sure to check your spam or junk folder.",
          )}
        </p>
      </div>

      {/* Back to Login */}
      <div className="mt-8 pt-6 border-t">
        <p className="text-sm text-muted-foreground mb-3">
          {t(
            "auth.registrationSuccess.alreadyVerified",
            "Already verified your email?",
          )}
        </p>
        <Button asChild variant="default" className="w-full">
          <Link to="/login">
            {t("auth.registrationSuccess.goToLogin", "Go to Login")}
            <ArrowRight className="w-4 h-4 ml-2" />
          </Link>
        </Button>
      </div>
    </div>
  );
};

export default RegistrationSuccess;
